import React, { ButtonHTMLAttributes } from 'react';
import { KEY_BOARD } from 'constants/general';
import COLOR from 'constants/color';

type SwitchSize = 'small' | 'medium' | 'large';
type SwitchVariant = 'default' | 'outlined' | 'filled' | 'minimal';

interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  size?: SwitchSize;
  variant?: SwitchVariant;
  labelPosition?: 'left' | 'right';
  className?: string;
  labelClassName?: string;
}

const ToggleIcon = ({ isVisible }: { isVisible: boolean }) =>
  isVisible ? (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      fill={COLOR.GRAY['900']}
      viewBox='0 0 24 24'
      className={`absolute inset-0 w-full h-full p-1 transition-opacity scale-125 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}>
      <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'></path>
    </svg>
  ) : (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox='0 0 24 24'
      className={`absolute inset-0 w-full h-full p-1 transition-opacity scale-125 ${
        !isVisible ? 'opacity-100' : 'opacity-0'
      }`}>
      <path
        d='M18 6L6 18M6 6l12 12'
        stroke={COLOR.GRAY['900']}
        strokeWidth='2.5'
        strokeLinecap='round'
      />
    </svg>
  );

const Switch: React.FC<SwitchProps> = ({
  checked = false,
  onChange,
  label,
  size = 'medium',
  variant = 'default',
  labelPosition = 'right',
  disabled = false,
  className = '',
  labelClassName = '',
  ...rest
}) => {
  const sizeClasses = {
    small: {
      track: '~w-7/9 ~h-3.5/4.5',
      thumb: '~w-2.5/3 ~h-2.5/3',
      label: '~text-xs/sm',
      gap: '~gap-1.5/2'
    },
    medium: {
      track: '~w-8/10 ~h-4/[1.375rem]',
      thumb: '~w-3/4 ~h-3/4',
      label: '~text-sm/base',
      gap: '~gap-2/2.5'
    },
    large: {
      track: '~w-10/12 ~h-5/6',
      thumb: '~w-4/5 ~h-4/5',
      label: '~text-base/lg',
      gap: '~gap-2.5/3'
    }
  };

  const variantClasses = {
    default: {
      trackChecked: 'bg-primary-500 border-primary-500',
      trackUnchecked: 'bg-primary-100 border-primary-100',
      thumbChecked: 'bg-white',
      thumbUnchecked: 'bg-primary-300',
      shadow: 'shadow'
    },
    outlined: {
      trackChecked: 'bg-transparent border-2 border-primary-500',
      trackUnchecked:
        'bg-transparent border-2 border-primary-300/40 dark:border-primary-400/30',
      thumbChecked: 'bg-primary-500',
      thumbUnchecked: 'bg-primary-300',
      shadow: 'shadow-sm'
    },
    filled: {
      trackChecked: 'bg-primary-500 border-primary-500',
      trackUnchecked: 'bg-primary-50 dark:bg-secondary-dark-400 border-primary-300/40 dark:border-secondary-dark-500',
      thumbChecked: 'bg-white',
      thumbUnchecked: 'bg-primary-200',
      shadow: 'shadow-sm'
    },
    minimal: {
      trackChecked: 'bg-primary-500/80 border-primary-500/60',
      trackUnchecked: 'bg-primary-100/50 border-primary-300/30',
      thumbChecked: 'bg-white',
      thumbUnchecked: 'bg-primary-300/80',
      shadow: ''
    }
  };

  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (
      [KEY_BOARD.ENTER.KEY, KEY_BOARD.SPACE.KEY].includes(event.key) &&
      !disabled &&
      onChange
    ) {
      event.preventDefault();
      onChange(!checked);
    }
  };

  const trackClasses = `relative inline-block ${currentSize.track} transition-all duration-200 ease-in-out rounded-full border ${
    checked
      ? `${currentVariant.trackChecked} ${currentVariant.shadow}`
      : `${currentVariant.trackUnchecked} ${currentVariant.shadow}`
  } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;

  const thumbClasses = `absolute left-1 top-1/2 -translate-y-1/2 ${currentSize.thumb} rounded-full transition-transform duration-200 ease-in-out ${
    checked
      ? `translate-x-full ${currentVariant.thumbChecked}`
      : `translate-x-0 ${currentVariant.thumbUnchecked}`
  }`;

  const containerClasses = `flex items-center ${currentSize.gap} ${
    labelPosition === 'left' ? 'flex-row-reverse' : ''
  } ${disabled ? 'opacity-50' : ''} ${className}`;

  const labelClasses = `${currentSize.label} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${labelClassName}`;

  return (
    <div className={containerClasses}>
      <button
        type='button'
        role='switch'
        aria-checked={checked}
        aria-disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={trackClasses}
        {...rest}>
        <span className={thumbClasses}>
          <ToggleIcon isVisible={checked ?? false} />
        </span>
      </button>
      {label && (
        <label
          onClick={!disabled ? handleClick : undefined}
          onKeyDown={
            !disabled
              ? (event: React.KeyboardEvent<HTMLLabelElement>) => {
                  if (
                    [KEY_BOARD.ENTER.KEY, KEY_BOARD.SPACE.KEY].includes(
                      event.key
                    )
                  ) {
                    event.preventDefault();
                    handleClick();
                  }
                }
              : undefined
          }
          className={labelClasses}
          htmlFor={rest.id}>
          {label}
        </label>
      )}
    </div>
  );
};

export default Switch;
